import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, map, distinctUntilChanged } from 'rxjs';

/**
 * ListStateService manages URL query parameter synchronization for pagination.
 *
 * Usage:
 * 1. Inject the service and ActivatedRoute in your list component
 * 2. Subscribe to getPageFromRoute() to react to URL page changes
 * 3. Call setPage() when pagination changes to update the URL
 * 4. Call clearPage() when filters change to reset to page 1
 *
 * Features:
 * - Backward compatible: URLs without ?page=X default to page 1
 * - Uses replaceUrl to avoid polluting browser history
 * - Preserves other query params when updating page
 */
@Injectable({
  providedIn: 'root'
})
export class ListStateService {
  private readonly router = inject(Router);

  /**
   * Read page number from URL query params.
   * Defaults to 1 if not present (backward compatible).
   *
   * @param route The ActivatedRoute from the component
   * @returns Observable that emits page number whenever it changes
   */
  getPageFromRoute(route: ActivatedRoute): Observable<number> {
    return route.queryParams.pipe(
      map(params => {
        const pageParam = params['page'];
        if (!pageParam) {
          return 1;
        }
        const page = parseInt(pageParam, 10);
        return isNaN(page) || page < 1 ? 1 : page;
      }),
      distinctUntilChanged()
    );
  }

  /**
   * Update URL with new page number.
   * Uses replaceUrl to avoid polluting browser history.
   * Preserves other query params.
   *
   * @param page The page number to set (1-indexed)
   */
  setPage(page: number): void {
    const queryParams = page > 1 ? { page: page.toString() } : { page: null };

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * Clear page param from URL (reset to page 1).
   * Used when filters change to reset pagination.
   */
  clearPage(): void {
    this.router.navigate([], {
      queryParams: { page: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * Get multiple list state params from URL.
   * Can be extended to support filters in URL.
   *
   * @param route The ActivatedRoute from the component
   * @param paramKeys Additional param keys to extract
   * @returns Observable with page and any additional params
   */
  getListStateFromRoute(
    route: ActivatedRoute,
    paramKeys: string[] = []
  ): Observable<{ page: number; params: Record<string, string | null> }> {
    return route.queryParams.pipe(
      map(params => {
        const pageParam = params['page'];
        let page = 1;
        if (pageParam) {
          const parsed = parseInt(pageParam, 10);
          page = isNaN(parsed) || parsed < 1 ? 1 : parsed;
        }

        const extractedParams: Record<string, string | null> = {};
        for (const key of paramKeys) {
          extractedParams[key] = params[key] ?? null;
        }

        return { page, params: extractedParams };
      }),
      distinctUntilChanged((prev, curr) =>
        prev.page === curr.page &&
        JSON.stringify(prev.params) === JSON.stringify(curr.params)
      )
    );
  }

  /**
   * Update multiple list state params in URL.
   *
   * @param params Object with param key-value pairs to set
   */
  setListState(params: Record<string, string | number | null>): void {
    const queryParams: Record<string, string | null> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) {
        queryParams[key] = null;
      } else if (key === 'page' && value === 1) {
        // Don't include page=1 in URL (cleaner URLs, backward compatible)
        queryParams[key] = null;
      } else {
        queryParams[key] = value.toString();
      }
    }

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
